import { isEditorOnly } from '../types/node'
import { MAX_SUBGRAPH_DEPTH } from './subgraph'
import type { SubGraph } from '../types/node'
/**
 * 节点图求值层 —— 纯函数，零 Vue / 零图引擎依赖。
 *
 * 这是架构里的「求值层」：把节点图 + 数据 算成每个输出端口的值。
 * 刻意做成纯函数，原因和 connectionRules / rows 一样：
 *   · 能脱离 UI 直接单测
 *   · 将来同一份逻辑要跑在浏览器 / Node / C++(QuickJS) 四个宿主上，不能依赖 DOM
 *
 * 设计要点：
 *   1. **节点求值器按类型注册**（`NodeEvaluator`），新增节点类型不用改这里
 *   2. **沿边回溯 + 记忆化**：一个节点只算一次，扇出到 N 个下游不重复计算
 *   3. **有环保护**：即使编辑器放过了环，求值也不会栈溢出
 *   4. **静音生效**：`model.muted` 的节点输出视为 NULL，下游回退内联默认值
 */
import type {
  GraphNodeModel,
  NodeDiagnostic,
  NodeRow,
  RepeaterRow,
  SocketDef,
} from '../types/node'
import type { SocketType } from '../types/socket'
import {
  connectedRepeaterCount,
  repeaterCountFor,
  repeaterHandle,
  repeaterIndexOf,
} from './rows'

/** 求值时只关心边的两端与 handle */
export interface GraphEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
  /** 静音的连线按「不存在」处理，下游回退内联默认值 */
  muted?: boolean
}

/**
 * 暴露给节点求值器的读取接口。
 *
 * 求值器**不应该**自己去翻 rows / edges —— 那样每个求值器都要重复处理
 * 「沿边回溯、内联兜底、变参展开」这些事。全部收敛到这里。
 */
export interface NodeEvalApi {
  readonly model: GraphNodeModel
  /** 数据源表：数据源名 → 字段名 → 值 */
  readonly data: Record<string, Record<string, unknown>>
  /**
   * 读一个输入端口的值。
   * 有连线 → 沿边回溯取上游输出；无连线 → 回退该行内联控件的值。
   */
  input(handleId: string): unknown
  /** 读变参行的全部实例值（按序号；未连接的位置是 undefined） */
  repeat(rowId: string): unknown[]
  /** 读某个属性行 / 输入行的内联控件值（不看连线） */
  literal(rowId: string): unknown
  /** 某个 handle 对应的端口类型 */
  socketType(handleId: string): SocketType | undefined
  /**
   * 跑一段**子图**（组 / 循环节点用）。
   *
   * 这就是「节点内部还有一张图」的求值入口：
   *   · 组节点   = 把输入喂进去、取输出节点的值
   *   · 循环节点 = 对着列表每一项跑一遍，收集累记值
   *
   * `seeds` 是**预置值**：这些节点的输出直接取给定值、不走自己的求值器。
   * 循环每次迭代把 `item` / `index` 灌给「循环起始节点」就走这条路。
   *
   * 返回内层的 `nodeId → handleId → 值`。内层 id 只在**本层**唯一，
   * 别和外层混着用。
   */
  runSubgraph(
    graph: SubGraph,
    seeds?: Record<string, Record<string, unknown>>,
  ): Map<string, Map<string, unknown>>
}

export type NodeEvaluator = (api: NodeEvalApi) => Record<string, unknown>

export interface EvalOptions {
  /** 数据源内容 */
  data?: Record<string, Record<string, unknown>>
  /** 节点类型（model.type）→ 求值器 */
  evaluators: Record<string, NodeEvaluator>
  /**
   * 预置值：这些节点的输出直接取给定值，不求值。
   *
   * 循环节点每次迭代把 `item` / `index` 灌给内部的「循环起始节点」，走的就是这条路。
   */
  seeds?: Record<string, Record<string, unknown>>
  /**
   * 把这一层产生的诊断**归到某个节点上**。
   *
   * 子图用：内层节点在顶层画布上根本看不见，诊断挂它身上没人看得到；
   * 归到包含它的组节点上，徽标才会亮。
   */
  attributeTo?: string
  /** 嵌套深度（内部用，防环） */
  depth?: number
  /**
   * 内部用：把诊断**同时**汇总到这里。
   *
   * 子图递归时共享外层那个数组 —— 不然内层 `evaluateGraph` 报的错
   * 跟着它自己的返回值一起被丢掉，外层一个诊断都看不到：
   * 组里坏了，顶层却不亮徽标。
   */
  sink?: Diagnostic[]
}

/** 诊断：在 NodeDiagnostic 上补一个归属节点 */
export interface Diagnostic extends NodeDiagnostic {
  /** 相关节点。无法归属到具体节点时为 undefined */
  nodeId?: string
}

export interface EvalResult {
  /** nodeId → handleId → 值。没有值的端口不会出现在 map 里 */
  outputs: Map<string, Map<string, unknown>>
  /**
   * 求值过程中的诊断。**带 nodeId**，所以宿主能给对应节点挂错误徽标 ——
   * 只有一行状态栏文字的话，节点一多就找不到是哪个出的问题。
   */
  diagnostics: Diagnostic[]
}

/* ==========================================================================
   辅助
   ========================================================================== */

/** 找出 handle 所属的行（含变参实例） */
function findRowByHandle(
  model: GraphNodeModel,
  handleId: string,
): NodeRow | undefined {
  for (const row of model.rows) {
    if (row.kind === 'input' || row.kind === 'output') {
      if (row.socket.id === handleId) return row
      continue
    }
    if (row.kind === 'repeater' && repeaterIndexOf(row, handleId) >= 0) {
      return row
    }
  }
  return undefined
}

/** 行的内联控件值 */
function widgetValueOf(row: NodeRow | undefined): unknown {
  if (row && (row.kind === 'property' || row.kind === 'input')) {
    return row.widget?.value
  }
  return undefined
}

/** 端口定义 */
function socketDefOf(
  model: GraphNodeModel,
  handleId: string,
): SocketDef | undefined {
  const row = findRowByHandle(model, handleId)
  if (!row) return undefined
  if (row.kind === 'input' || row.kind === 'output') return row.socket
  if (row.kind === 'repeater') {
    const index = repeaterIndexOf(row, handleId)
    return { id: handleId, type: row.socketType, connected: Boolean(row.connected?.[index]) }
  }
  return undefined
}

/* ==========================================================================
   主流程
   ========================================================================== */

export function evaluateGraph(
  models: readonly GraphNodeModel[],
  edges: readonly GraphEdge[],
  options: EvalOptions,
): EvalResult {
  const data = options.data ?? {}
  const seeds = options.seeds ?? {}
  const depth = options.depth ?? 0
  const modelById = new Map(models.map((m) => [m.id, m]))

  /** target nodeId → targetHandle → 上游 (nodeId, handle) */
  const incoming = new Map<string, Map<string, GraphEdge>>()
  for (const edge of edges) {
    // 静音边直接不进索引 —— 效果就是「这条线不存在」，
    // 下游 readInput 会自然回退到自己的内联默认值。
    if (edge.muted) continue
    let byHandle = incoming.get(edge.target)
    if (!byHandle) {
      byHandle = new Map()
      incoming.set(edge.target, byHandle)
    }
    byHandle.set(edge.targetHandle ?? '', edge)
  }

  const memo = new Map<string, Record<string, unknown>>()
  const visiting = new Set<string>()
  const diagnostics: Diagnostic[] = []

  /**
   * 推一条诊断。
   *
   * 有 `attributeTo` 时改挂到那个节点上（子图的诊断归到组节点），
   * 并在文案前补上内层 id —— 否则「组里某个东西没注册求值器」
   * 这种提示根本没法定位。
   */
  function report(entry: { nodeId?: string; severity: 'error' | 'warning'; message: string }) {
    const owner = options.attributeTo
    const final = owner
      ? {
          nodeId: owner,
          severity: entry.severity,
          message: entry.nodeId ? `[组内 ${entry.nodeId}] ${entry.message}` : entry.message,
        }
      : entry

    diagnostics.push(final)
    // 子图递归时共享外层的数组（见 EvalOptions.sink 的说明）
    if (options.sink && options.sink !== diagnostics) options.sink.push(final)
  }

  function evalNode(nodeId: string): Record<string, unknown> | undefined {
    const cached = memo.get(nodeId)
    if (cached) return cached

    /*
     * 预置值优先。
     * 循环每次迭代把 item / index 直接灌进「循环起始节点」——
     * 那个节点自己的求值器不该跑（它没有输入可读）。
     */
    const seeded = seeds[nodeId]
    if (seeded) {
      memo.set(nodeId, seeded)
      return seeded
    }

    if (visiting.has(nodeId)) {
      report({ nodeId, severity: 'error', message: '检测到环，该分支已跳过' })
      return undefined
    }

    const model = modelById.get(nodeId)
    if (!model) return undefined

    /*
     * 静音：输出视为 NULL（v5 契约）。
     *
     * 返回**空对象**而不是 undefined —— 空对象表示「这个节点算过了，但没有值」，
     * 下游 readInput 会因此回退到自己的内联默认值，
     * 正是契约里写的「muted → 下游回退静态值」。
     */
    if (model.muted) {
      const empty: Record<string, unknown> = {}
      memo.set(nodeId, empty)
      return empty
    }

    /*
     * 编辑器专用节点（框 / 注释）：不产出值，也**不报「未注册求值器」**。
     * 不跳过的话，一个纯装饰的框上会挂个告警徽标，看着像图坏了。
     *
     * 返回空对象（跟静音同样的处理）：表示「算过了，但没有值」，
     * 下游会因此回退到自己的内联默认值。
     */
    if (isEditorOnly(model)) {
      const empty: Record<string, unknown> = {}
      memo.set(nodeId, empty)
      return empty
    }

    const evaluator = options.evaluators[model.type]
    if (!evaluator) {
      /*
       * ⚠️ 文案里**要说「可能是包没装」**。
       *
       * 节点包是可装卸的，而文档里逐节点存了行结构 —— 所以包卸掉之后，
       * 节点**照样画得出来**，只是算不出值。这时候一句干巴巴的
       * 「未注册求值器」会把人引向「编辑器坏了」，
       * 而真实原因通常是「这个节点所属的包没装」。
       */
      report({
        nodeId,
        severity: 'warning',
        message:
          `未注册求值器的节点类型：${model.type}` +
          `（节点照常显示，但算不出值 —— 通常是它所属的**节点包**没装；` +
          `在「视图 → 节点包」里装上，或者换成别的节点）`,
      })
      return undefined
    }

    visiting.add(nodeId)
    try {
      const result = evaluator(makeApi(model, evalNode))
      memo.set(nodeId, result)
      return result
    } catch (error) {
      report({
        nodeId,
        severity: 'error',
        message: `求值失败：${error instanceof Error ? error.message : String(error)}`,
      })
      return undefined
    } finally {
      visiting.delete(nodeId)
    }
  }

  function makeApi(
    model: GraphNodeModel,
    evaluate: (id: string) => Record<string, unknown> | undefined,
  ): NodeEvalApi {
    const byHandle = incoming.get(model.id)

    /*
     * ══ 集合边：带偏移的合并（docs/collection-ports.md §3.4） ══
     *
     * 一条集合边会往目标的**一整片**实例上落值，所以「读某一个句柄」这件事
     * 不能只看那一个句柄的入边 —— 要先看这条边的来源是不是一条变参行。
     *
     * ⚠️ `memoizedInstances` 单独记一层：一个源变参行的那些件
     * 会被目标侧的每一件各读一次（N 件 → N 次），不记的话
     * 每次都要重算一遍上游的输出（扇出场景下是 O(N²) 次上游求值）。
     */
    /**
     * 读一个输入端口的值。
     *
     * 一条边 = 一个值：沿边回溯取上游那个 handle 的输出；没连线（或上游
     * 没产出值）就回退到**本行的内联控件值**。
     *
     * ⚠️ 这里曾经长出一整套「集合边的带偏移合并」（`rowMerge` /
     * `primeMerges` / `collectionMergeAt` / `upstreamInstancesOf`）——
     * 那是为「变参行的集合面」服务的。**集合这个类型已经删掉**，
     * 所以整段一并删除：边不再有第二种含义，读一个端口就是一个值。
     */
    function readInput(handleId: string): unknown {
      const edge = byHandle?.get(handleId)
      if (edge) {
        const upstream = evaluate(edge.source)
        const value = upstream?.[edge.sourceHandle ?? '']
        // 上游确实产出了值 → 用它
        if (value !== undefined) return value
        // 上游没产出值（被静音 / 未注册求值器 / 出错 / 遇环）
        // → 落到下面回退本行的内联默认
      }
      // 无连线（或上游无值）→ 回退到该行的内联控件值
      return widgetValueOf(findRowByHandle(model, handleId))
    }

    /**
     * 读一个变参实例 —— 就是「实例序号 → 句柄」这一步。
     */
    function readInstance(row: RepeaterRow, index: number): unknown {
      return readInput(repeaterHandle(row, index))
    }

    /**
     * 变参行「最后一个被连接的实例序号」（**按边推导**，不看视图状态）。
     *
     * 输入方向的件数就是靠它长出来的：连到末尾那个空槽 → 多一路。
     * 读的是「哪些句柄上真的挂着边」，所以 headless 求值也准。
     */
    function lastConnectedOf(row: RepeaterRow): number {
      let last = connectedRepeaterCount(row) - 1
      if (byHandle) {
        for (const handleId of byHandle.keys()) {
          const index = repeaterIndexOf(row, handleId)
          if (index > last) last = index
        }
      }
      return last
    }

    return {
      model,
      data,
      input: readInput,
      repeat(rowId: string) {
        const found = model.rows.find((r) => r.id === rowId)
        if (!found || found.kind !== 'repeater') return []
        const row = found as RepeaterRow

        /*
         * ⚠️ 实例数量**不能只信 `row.connected`**。
         *
         * 那个数组是编辑器维护的视图状态（由边同步而来）。headless 求值时
         * 它可能是空的，于是变参行只会暴露 `min` 个实例，
         * 接在第 2 个之后的线全部丢失 —— 这是测试抓出来的真实缺陷。
         *
         * 所以数量从**边**推导，再套用与渲染端同一套 min/max 钳制
         * （`repeaterCountFor`）：两处共用一条规则，实例数才不会两边不一致。
         *
         * ⚠️ **两个方向的件数都由 `repeaterCountFor` 一个函数说了算**：
         * 它对**输出方向**只认声明（`count` / `min`），传进去的
         * `lastConnected` 会被忽略 —— 所以这里**不需要**按方向分支。
         * （我一度加过 `isOutputRepeater(row) ? … : …` 那个分支，
         * 实测发现它对任何输入都给出同一个结果，是**死代码**，已删。
         * 契约 §3.1 那条「输出只由声明决定」本来就落在 `repeaterCountFor` 里。）
         *
         * ⚠️ 这里真正的**回归**不是分支缺失，而是曾经叠在它上面的
         * 「集合合并」那套：它用 `instanceCountOf`（**只信边**）再钳一次，
         * 于是声明 3 路、一个边都没有的输出变参行被钳成 0 件 ——
         * 节点拿不到自己声明的那几件。删掉那套之后这条路自然就通了。
         */
        const total = repeaterCountFor(row, lastConnectedOf(row))

        const values: unknown[] = []
        for (let i = 0; i < total; i += 1) {
          values.push(readInstance(row, i))
        }
        return values
      },
      literal(rowId: string) {
        return widgetValueOf(model.rows.find((r) => r.id === rowId))
      },
      socketType(handleId: string) {
        return socketDefOf(model, handleId)?.type
      },
      /*
       * 跑子图 = 用同一套求值器、同一份数据，对**另一张图**再走一遍。
       *
       * 递归调用公开入口（而不是另抽一层内部函数）是有意的：
       * 子图和外层走的是**完全同一条路径**，不存在「顶层和组内行为不一致」。
       * 记忆表是每层一份（每次调用新建），所以内外层 id 撞名也不会串。
       */
      runSubgraph(graph: SubGraph, subSeeds?: Record<string, Record<string, unknown>>) {
        if (depth >= MAX_SUBGRAPH_DEPTH) {
          report({
            nodeId: model.id,
            severity: 'error',
            message: `子图嵌套超过 ${MAX_SUBGRAPH_DEPTH} 层，已停止（可能是自含的组）`,
          })
          return new Map<string, Map<string, unknown>>()
        }
        return evaluateGraph(graph.nodes, graph.edges, {
          data,
          evaluators: options.evaluators,
          seeds: subSeeds,
          // 内层的问题归到**包含它的那个节点**上，否则顶层看不到
          attributeTo: options.attributeTo ?? model.id,
          depth: depth + 1,
          // 诊断汇总到最外层那个数组，不然内层的报错会跟着返回值一起被丢掉
          sink: options.sink ?? diagnostics,
        }).outputs
      },
    }
  }

  const outputs = new Map<string, Map<string, unknown>>()
  for (const model of models) {
    const result = evalNode(model.id)
    if (!result) continue
    const byHandle = new Map<string, unknown>()
    for (const [handle, value] of Object.entries(result)) {
      if (value !== undefined) byHandle.set(handle, value)
    }
    outputs.set(model.id, byHandle)
  }

  return { outputs, diagnostics }
}

/* ==========================================================================
   展示辅助（调试节点用）
   ========================================================================== */

/** 值的类型名，用于调试面板上的类型徽标 */
export function valueKind(value: unknown): string {
  if (value === undefined) return 'unset'
  if (value === null) return 'null'
  if (Array.isArray(value)) return `list[${value.length}]`
  if (typeof value === 'object') {
    const kind = (value as { kind?: unknown }).kind
    return typeof kind === 'string' ? kind : 'object'
  }
  return typeof value
}

/** 把值格式化成一行可读文本 */
export function formatValue(value: unknown): string {
  if (value === undefined) return '—'
  if (value === null) return 'null'
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'true' : 'false'

  if (typeof value === 'number') {
    if (Number.isInteger(value)) return String(value)
    return value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
  }

  if (Array.isArray(value)) {
    return `[${value.map((v) => formatValue(v)).join(', ')}]`
  }

  if (typeof value === 'object') {
    /*
     * ⚠️ 这里原来有一段"组件实例就显示它渲染出来的文本"的特判 ——
     * 它**认识某个包的字段名**（`text` / `fontWeight` / `fontColor`），
     * 而那些字段早就改名了（现在是 `props.text` 那种），于是那段代码
     * 对打印组件只会返回**空字符串**（比 JSON 还糟：看不出是什么）。
     *
     * 两个教训：
     *   · 内核不该认识领域的字段名（改了包，内核这边**静默失效**）；
     *   · 想给某类值一个更好的显示，应该由**包**来说（比如值自己带一个
     *     `display` 字段），而不是内核猜。
     * 现在统一 JSON：至少它**是对的**，长不长是界面截断的事。
     */
    return JSON.stringify(value)
  }

  return String(value)
}
